import { BigQueryOptionsView } from "@app/components/data_source/BigQueryOptionsView";
import { createConnectorOptionsPdfEnabled } from "@app/components/data_source/ConnectorOptionsPdfEnabled";
import { GithubCodeEnableView } from "@app/components/data_source/GithubCodeEnableView";
import { GongOptionComponent } from "@app/components/data_source/gong/GongOptionComponent";
import { IntercomConfigView } from "@app/components/data_source/IntercomConfigView";
import { MicrosoftOAuthExtraConfig } from "@app/components/data_source/MicrosoftOAuthExtraConfig";
import { SalesforceOauthExtraConfig } from "@app/components/data_source/salesforce/SalesforceOAuthExtractConfig";
import { SlackOAuthExtraConfig } from "@app/components/data_source/SlackOAuthExtraConfig";
import { ZendeskConfigView } from "@app/components/data_source/ZendeskConfigView";
import { ZendeskOAuthExtraConfig } from "@app/components/data_source/ZendeskOAuthExtraConfig";
import { SensitivityLabelsConfig } from "@app/components/shared/labels/SensitivityLabelsConfig";
import type { SensitivityLabelsController } from "@app/components/shared/labels/types";
import { CONNECTOR_METADATA } from "@app/lib/connector_metadata";
import type { ConnectorPermission } from "@app/types/connectors/connectors_api";
import type { ConnectorProvider, DataSourceType } from "@app/types/data_source";
import type { PlanType } from "@app/types/plan";
import type { LightWorkspaceType, WorkspaceType } from "@app/types/user";
import {
  BigQueryLogo,
  ConfluenceLogo,
  DriveLogo,
  DustLogoSquare,
  Folder,
  GithubLogo,
  Globe01,
  GongLogo,
  IntercomLogo,
  MicrosoftLogo,
  NotionLogo,
  SalesforceLogo,
  SlackLogo,
  SnowflakeLogo,
  ZendeskLogo,
} from "@dust-tt/sparkle";
import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import type React from "react";
import type { ComponentType } from "react";

export interface ConnectorOptionsProps {
  owner: WorkspaceType;
  readOnly: boolean;
  isAdmin: boolean;
  dataSource: DataSourceType;
  plan: PlanType;
}

interface ConnectorAdvancedOptionsProps {
  owner: LightWorkspaceType;
  readOnly: boolean;
  controller: SensitivityLabelsController;
}

export interface ConnectorOauthExtraConfigProps {
  extraConfig: Record<string, string>;
  setExtraConfig: (
    value:
      | Record<string, string>
      | ((prev: Record<string, string>) => Record<string, string>)
  ) => void;
  setIsExtraConfigValid: (valid: boolean) => void;
}

type ConnectorPermissionsConfigurable =
  | {
      isPermissionsConfigurableBlocked: true;
      permissionsDisabledPlaceholder: MessageDescriptor;
    }
  | {
      isPermissionsConfigurableBlocked?: never;
    };

type ConnectorProviderUIDetails = {
  hide: boolean;
  getLogoComponent: (
    isDark?: boolean
  ) => (props: React.SVGProps<SVGSVGElement>) => React.JSX.Element;
  optionsComponent?: ComponentType<ConnectorOptionsProps>;
  advancedOptionsComponent?: ComponentType<ConnectorAdvancedOptionsProps>;
  description: string;
  mismatchError: MessageDescriptor;
  limitations: MessageDescriptor | null;
  oauthExtraConfigComponent?: (
    props: ConnectorOauthExtraConfigProps
  ) => React.JSX.Element;
  guideLink: string | null;
  selectLabel?: MessageDescriptor; // Show in the permissions modal, above the content node tree, note that a connector might not allow to select anything
  emptyNodeLabel?: MessageDescriptor;
  isNested: boolean;
  isTitleFilterEnabled?: boolean;
  isResourceSelectionDisabled?: boolean; // Whether the user cannot select distinct resources (everything is synced).
  permissions: {
    selected: ConnectorPermission;
    unselected: ConnectorPermission;
  };
  isHiddenAsDataSource?: boolean;
} & ConnectorPermissionsConfigurable;

// TODO(slack 2025-06-19): Remove this function once the new app is published.
export function getConnectorPermissionsConfigurableBlocked(
  provider?: ConnectorProvider | null
): { blocked: boolean; placeholder?: MessageDescriptor } {
  if (!provider) {
    return { blocked: false };
  }

  const connectorConfig = CONNECTOR_UI_CONFIGURATIONS[provider];
  const isBlocked = connectorConfig.isPermissionsConfigurableBlocked;

  if (!isBlocked) {
    return { blocked: false };
  }

  return {
    blocked: true,
    placeholder: connectorConfig.permissionsDisabledPlaceholder,
  };
}

export const isConnectorPermissionsEditable = (
  provider?: ConnectorProvider | null
): boolean => {
  if (!provider) {
    return false;
  }

  return (
    CONNECTOR_UI_CONFIGURATIONS[provider].permissions.selected !== "none" ||
    CONNECTOR_UI_CONFIGURATIONS[provider].permissions.unselected !== "none"
  );
};

export const CONNECTOR_UI_CONFIGURATIONS: Record<
  ConnectorProvider,
  ConnectorProviderUIDetails
> = {
  confluence: {
    ...CONNECTOR_METADATA.confluence,
    hide: false,
    limitations: msg`Dust indexes pages in selected global spaces without any view restrictions. If a page, or its parent pages, have view restrictions, it won't be indexed.`,
    mismatchError: msg`You cannot select another Confluence domain.\nPlease contact us at support@dust.tt if you initially selected the wrong domain.`,
    selectLabel: msg`Select pages`,
    getLogoComponent: () => {
      return ConfluenceLogo;
    },
    isNested: true,
    permissions: {
      selected: "read",
      unselected: "none",
    },
  },
  notion: {
    ...CONNECTOR_METADATA.notion,
    hide: false,
    limitations: msg`External files and content behind links are not indexed.`,
    mismatchError: msg`You cannot select another Notion workspace.\nPlease contact us at support@dust.tt if you initially selected a wrong workspace.`,
    selectLabel: msg`Synchronized content`,
    getLogoComponent: () => {
      return NotionLogo;
    },
    isNested: true,
    permissions: {
      selected: "none",
      unselected: "none",
    },
  },
  google_drive: {
    ...CONNECTOR_METADATA.google_drive,
    hide: false,
    limitations: msg`Files with empty text content or with more than 750KB of extracted text are ignored. By default, PDF files are not indexed. Email us at support@dust.tt to enable PDF indexing.`,
    mismatchError: msg`You cannot select another Google Drive domain.\nPlease contact us at support@dust.tt if you initially selected a wrong shared Drive.`,
    selectLabel: msg`Select folders and files`,
    getLogoComponent: () => {
      return DriveLogo;
    },
    optionsComponent: createConnectorOptionsPdfEnabled(
      msg`When enabled, PDF documents from your Google Drive will be synced and processed by Dust.`
    ),
    isNested: true,
    permissions: {
      selected: "read",
      unselected: "none",
    },
  },
  slack: {
    ...CONNECTOR_METADATA.slack,
    // TODO(slack 2025-06-19): Hide the Slack connector until we publish the new app.
    hide: true,
    limitations: msg`External files and content behind links are not indexed.`,
    mismatchError: msg`You cannot select another Slack team.\nPlease contact us at support@dust.tt if you initially selected the wrong team.`,
    selectLabel: msg`Select channels`,
    getLogoComponent: () => {
      return SlackLogo;
    },
    oauthExtraConfigComponent: SlackOAuthExtraConfig,
    isNested: false,
    isTitleFilterEnabled: true,
    permissions: {
      selected: "read_write",
      unselected: "write",
    },
  },
  slack_bot: {
    // Hidden from connections since used as bot integration only. Strings below are therefore all
    // set to N/A
    hide: true,
    isPermissionsConfigurableBlocked: true,
    permissionsDisabledPlaceholder: msg`N/A`,
    description: "N/A",
    limitations: msg`N/A`,
    mismatchError: msg`You cannot select another Slack team.\nPlease contact us at support@dust.tt if you initially selected the wrong team.`,
    guideLink: "https://docs.dust.tt/docs/slack-connection",
    selectLabel: msg`N/A`,
    getLogoComponent: () => {
      return SlackLogo;
    },
    isNested: false,
    isTitleFilterEnabled: true,
    permissions: {
      selected: "read_write",
      unselected: "write",
    },
    isHiddenAsDataSource: true,
  },
  github: {
    ...CONNECTOR_METADATA.github,
    hide: false,
    limitations: msg`Dust gathers data from issues, discussions, and pull-requests (top-level discussion, but not in-code comments). It synchronizes your code only if enabled. At this time, Dust cannot sync code repositories over 10GB, or individual files over 4MB. Please contact support@dust.tt if you need to sync larger repositories.`,
    mismatchError: msg`You cannot select another GitHub organization.\nPlease contact us at support@dust.tt if you initially selected a wrong organization or if you completely uninstalled the GitHub app.`,
    selectLabel: msg`Authorized content`,
    getLogoComponent: () => {
      return GithubLogo;
    },
    optionsComponent: GithubCodeEnableView,
    isNested: true,
    permissions: {
      selected: "none",
      unselected: "none",
    },
  },
  intercom: {
    ...CONNECTOR_METADATA.intercom,
    hide: false,
    limitations: msg`Dust will index only the conversations from the selected Teams that were initiated within the past 90 days and concluded (marked as closed). For the Help Center data, Dust will index every Article published within a selected Collection.`,
    mismatchError: msg`You cannot select another Intercom workspace.\nPlease contact us at support@dust.tt if you initially selected a wrong workspace.`,
    selectLabel: msg`Select pages`,
    getLogoComponent: () => {
      return IntercomLogo;
    },
    optionsComponent: IntercomConfigView,
    isNested: true,
    permissions: {
      selected: "read",
      unselected: "none",
    },
  },
  microsoft: {
    ...CONNECTOR_METADATA.microsoft,
    hide: false,
    limitations: msg`Dust will only index documents accessible to the account used when making the connection. Only organizational accounts are supported (Sharepoint). At the time, OneDrive cannot be synced.`,
    mismatchError: msg`You cannot select another Microsoft account.\nPlease contact us at support@dust.tt if you initially selected a wrong account.`,
    selectLabel: msg`Select folders and files`,
    emptyNodeLabel: msg`Select the folder to enable file synchronization.`,
    getLogoComponent: () => {
      return MicrosoftLogo;
    },
    optionsComponent: createConnectorOptionsPdfEnabled(
      msg`When enabled, PDF documents from your Microsoft OneDrive and SharePoint will be synced and processed by Dust.`
    ),
    advancedOptionsComponent: SensitivityLabelsConfig,
    isNested: true,
    isTitleFilterEnabled: true,
    oauthExtraConfigComponent: MicrosoftOAuthExtraConfig,
    permissions: {
      selected: "read",
      unselected: "none",
    },
  },
  microsoft_bot: {
    hide: true,
    description:
      "Enable your Microsoft Teams bot integration to interact with Dust directly from Teams.",
    limitations: msg`Bot must be enabled in organization settings.`,
    mismatchError: msg`You cannot select another Microsoft tenant.\nPlease contact us at support@dust.tt if you initially selected a wrong tenant.`,
    guideLink: "https://docs.dust.tt/docs/dust-in-teams",
    selectLabel: msg`Bot configuration`,
    getLogoComponent: () => {
      return MicrosoftLogo;
    },
    isNested: false,
    oauthExtraConfigComponent: MicrosoftOAuthExtraConfig,
    permissions: {
      selected: "read_write",
      unselected: "write",
    },
    isHiddenAsDataSource: true,
  },
  webcrawler: {
    ...CONNECTOR_METADATA.webcrawler,
    hide: false,
    limitations: null,
    mismatchError: msg`You cannot change the URL. Please add a new public URL instead.`,
    getLogoComponent: () => {
      return Globe01;
    },
    isNested: true,
    permissions: {
      selected: "none",
      unselected: "none",
    },
  },
  snowflake: {
    ...CONNECTOR_METADATA.snowflake,
    hide: false,
    limitations: null,
    mismatchError: msg`You cannot change the Snowflake account. Please add a new Snowflake connection instead.`,
    getLogoComponent: () => {
      return SnowflakeLogo;
    },
    isNested: true,
    selectLabel: msg`Select tables`,
    permissions: {
      selected: "read",
      unselected: "none",
    },
  },
  zendesk: {
    ...CONNECTOR_METADATA.zendesk,
    hide: false,
    limitations: msg`Dust will index the content accessible to the authorized account only. Attachments are not indexed.`,
    mismatchError: msg`You cannot select another Zendesk workspace.\nPlease contact us at support@dust.tt if you initially selected a wrong workspace.`,
    getLogoComponent: () => {
      return ZendeskLogo;
    },
    optionsComponent: ZendeskConfigView,
    oauthExtraConfigComponent: ZendeskOAuthExtraConfig,
    isNested: true,
    permissions: {
      selected: "read",
      unselected: "none",
    },
  },
  bigquery: {
    ...CONNECTOR_METADATA.bigquery,
    hide: false,
    limitations: null,
    mismatchError: msg`You cannot change the BigQuery project. Please add a new BigQuery connection instead.`,
    getLogoComponent: () => {
      return BigQueryLogo;
    },
    optionsComponent: BigQueryOptionsView,
    isNested: true,
    selectLabel: msg`Select tables`,
    permissions: {
      selected: "read",
      unselected: "none",
    },
  },
  salesforce: {
    ...CONNECTOR_METADATA.salesforce,
    hide: true,
    limitations: null,
    mismatchError: msg`You cannot change the Salesforce instance URL. Please add a new Salesforce connection instead.`,
    getLogoComponent: () => {
      return SalesforceLogo;
    },
    oauthExtraConfigComponent: SalesforceOauthExtraConfig,
    isNested: true,
    permissions: {
      selected: "read",
      unselected: "none",
    },
  },
  gong: {
    ...CONNECTOR_METADATA.gong,
    isResourceSelectionDisabled: true,
    optionsComponent: GongOptionComponent,
    hide: false,
    getLogoComponent: () => {
      return GongLogo;
    },
    isNested: true,
    permissions: {
      selected: "read",
      unselected: "none",
    },
    limitations: msg`OAuth requires Gong administrator access. All transcripts from the workspace will be synchronized with Dust, except those marked as private in Gong. A Permission Profile can be selected to restrict synced calls to participants from that profile.`,
    mismatchError: msg`You cannot change the Gong account. Please add a new Gong connection instead.`,
  },
  dust_project: {
    hide: true,
    description: "Use Dust Pod as a data source.",
    limitations: null,
    mismatchError: msg`You cannot change the Dust Pod. Please add a new Dust Pod connection instead.`,
    guideLink: null,
    getLogoComponent: () => {
      return DustLogoSquare;
    },
    isNested: false,
    permissions: {
      selected: "read",
      unselected: "none",
    },
  },
};

export function getConnectorProviderLogoWithFallback({
  provider,
  fallback = Folder,
  isDark,
}: {
  provider: ConnectorProvider | null;
  fallback?: ComponentType;
  isDark?: boolean;
}): ComponentType {
  if (!provider) {
    return fallback;
  }
  return CONNECTOR_UI_CONFIGURATIONS[provider].getLogoComponent(isDark);
}
